import React, { ReactElement, useState, useEffect } from 'react';
import ButtonModalForm from '../ButtonModalForm';
import FirewallService, { AddRuleProps } from '../../services/FirewallService';
import CcxIconPlusCircleOutlined from '../ccx/icons/CcxIconPlusCircleOutlined';
import { notification } from 'antd';
import CcxIconCheckCircle from '../ccx/icons/CcxIconCheckCircle';
import CcxIconCloseCircleTwoTone from '../ccx/icons/CcxIconCloseCircleTwoTone';
import DeploymentsItem from '../../types/DeploymentsItem';
import CopyToClipboardText from '../CopyToClipboardText';
import styles from './AddFirewallRule.module.less';
import usePiwikPro from '../../core/hooks/usePiwikPro';
import Alert from '@severalnines/bar-frontend-components/build/lib/Feedback/Alert';
import {
    CLICK_EVENT,
    DATASTORE_ADDED_TRUSTED_SOURCE_FIREWALL_FAILED,
    DATASTORE_ADDED_TRUSTED_SOURCE_FIREWALL_SUCCESSFULLY,
} from '../../core/PiwikConstants';
import Can from '../../core/providers/Can';
import { CAN_ADD_FIREWALL } from '../../core/PermissionRoles';
import { Validator } from 'ip-num/Validator';
import { IPv4 } from 'ip-num/IPNumber';
import { IPv4CidrRange } from 'ip-num/IPRange';

type AddFirewallRuleProps = {
    uuid: string;
    onSuccess: Function;
    currentDeployment?: DeploymentsItem | null | undefined;
    ipAddress: string;
    testId?: string;
};

// Reserved IP ranges - defined outside for reuse
export const RESERVED_IP_RANGES = [
    '0.0.0.0/8',
    '127.0.0.0/8',
    '169.254.0.0/16',
    '198.51.100.0/24',
    '203.0.113.0/24',
    '224.0.0.0/4',
    '240.0.0.0/4',
    '255.255.255.255/32',
];

// Result type for the validation function
export type IpValidationResult = {
    isValid: boolean;
    invalidIps: string[];
    reservedIps: string[];
    errorMessage: string | null;
};

// Pure validation function exported for testing
export const validateIpAddresses = (values: string[]): IpValidationResult => {
    // Result object
    const result: IpValidationResult = {
        isValid: true,
        invalidIps: [],
        reservedIps: [],
        errorMessage: null,
    };

    // Handle null/undefined/empty values
    if (!values || !Array.isArray(values) || values.length === 0) {
        return result;
    }

    // Create range objects once outside the loop for better performance
    const reservedRanges = RESERVED_IP_RANGES.map((range) => {
        try {
            return IPv4CidrRange.fromCidr(range);
        } catch (error) {
            console.error(`Error creating range object for ${range}:`, error);
            return null;
        }
    }).filter((range): range is IPv4CidrRange => range !== null);

    // Process each IP
    for (const ip of values) {
        if (!ip || typeof ip !== 'string') continue;

        const trimmedIp = ip.trim();
        if (!trimmedIp) continue;

        // Check if we need to validate CIDR or just IP
        const hasCidr = trimmedIp.includes('/');
        let ipPart = trimmedIp;

        if (hasCidr) {
            // CIDR format validation
            const [isValid, message] =
                Validator.isValidIPv4CidrNotation(trimmedIp);
            if (!isValid) {
                result.invalidIps.push(`"${trimmedIp}" - ${message}`);
                continue;
            }
            ipPart = trimmedIp.split('/')[0];
        } else {
            // Plain IP format validation
            const [isValid, message] = Validator.isValidIPv4String(trimmedIp);
            if (!isValid) {
                result.invalidIps.push(`"${trimmedIp}" - ${message}`);
                continue;
            }
        }

        // If we got here, the format is valid - now check for reserved ranges
        try {
            const ipV4 = new IPv4(ipPart);

            // Check against each pre-created range
            const isReserved = reservedRanges.some(
                (range) =>
                    ipV4.isGreaterThanOrEquals(range.getFirst()) &&
                    ipV4.isLessThanOrEquals(range.getLast())
            );

            if (isReserved) {
                result.reservedIps.push(trimmedIp);
            }
        } catch (error) {
            // This should rarely happen since we already validated the format
            result.invalidIps.push(
                `"${trimmedIp}" - Error checking reserved status`
            );
        }
    }

    // Set result based on findings
    if (result.reservedIps.length > 0) {
        result.isValid = false;
        result.errorMessage = `Cannot use reserved IP ranges: ${result.reservedIps.join(
            ', '
        )}`;
    } else if (result.invalidIps.length > 0) {
        result.isValid = false;
        result.errorMessage = `Invalid IP address format: ${result.invalidIps.join(
            ', '
        )}`;
    }

    return result;
};

function AddFirewallRule({
    uuid,
    onSuccess,
    currentDeployment,
    ipAddress,
    testId = 'AddFirewallRule',
}: AddFirewallRuleProps): ReactElement {
    const { trackEvent } = usePiwikPro();
    const [form, setForm] = useState<any>(undefined);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [validationTimeout, setValidationTimeout] =
        useState<NodeJS.Timeout | null>(null);

    // Clean up timeout when component unmounts
    useEffect(() => {
        return () => {
            if (validationTimeout) {
                clearTimeout(validationTimeout);
            }
        };
    }, [validationTimeout]);

    // Component-specific validate function that updates state
    const validateIPs = (values: string[]): boolean => {
        // Use the exported validation function
        const validationResult = validateIpAddresses(values);

        // Update component state
        if (!validationResult.isValid) {
            setValidationError(
                validationResult.errorMessage || 'Invalid IP addresses'
            );
            return false;
        } else {
            // Make sure to clear the validation error when valid
            setValidationError(null);
            return true;
        }
    };

    // Override validation function for form submission
    const overrideValidation = () => {
        if (!form) return true;

        const values = form.getFieldValue('source');
        return validateIPs(values);
    };

    const handleFieldChange = (values: string[]) => {
        if (!form || !Array.isArray(values)) return;

        // Process even empty arrays - clear validation when user removes all entries
        const formattedValues = values
            .filter((value) => value && typeof value === 'string')
            .map((value) => value.trim())
            .filter(Boolean);

        // Debounce validation to prevent too many validations during rapid typing
        if (validationTimeout) {
            clearTimeout(validationTimeout);
        }

        // Always update the form values immediately with the cleaned values
        form.setFieldsValue({
            source: formattedValues,
        });

        // Clear previous validation errors immediately when user makes changes
        if (validationError) {
            setValidationError(null);
        }

        // Delay validation slightly to allow UI to update first
        const timeoutId = setTimeout(() => {
            validateIPs(formattedValues);
        }, 100);

        setValidationTimeout(timeoutId);
    };

    const fields = [
        {
            name: ['source'],
            testId: 'AddFirewallRuleSource',
            required: true,
            label: 'Sources',
            helpText: validationError || (
                <div className={styles.AddFirewallRuleSourceHelpText}>
                    Your IP:{' '}
                    <CopyToClipboardText text={ipAddress} hoverable={false} />
                </div>
            ),
            placeholder: 'Enter CIDR',
            type: 'multipleIp',
            defaultValue: [],
            onChange: handleFieldChange,
            validateStatus: validationError ? 'error' : undefined,
            tips: <>Source address CIDR, i.e.: 1.2.3.4/32</>,
            rules: [{ required: true, message: 'Sources is required.' }],
        },
        {
            name: ['description'],
            testId: 'AddFirewallRuleDescription',
            required: false,
            label: 'Description',
            tips: (
                <>
                    You can add a description for the rule, which can help you
                    identify it later. A description can be up to 255 characters
                    in length. Allowed characters are a-z, A-Z, 0-9, spaces, and
                    ._-:/()#,@[]+=;{}!$*
                </>
            ),
            maxLength: 255,
            validators: [
                {
                    message: `Allowed characters are a-z, A-Z, 0-9, spaces, and ._-:/()#,@[]+=;{}!$*`,
                    pattern:
                        /^[a-zA-Z0-9\s._\-\:\/\(\)#\,@\[\]\+\=\;\{\}!$\*]*$/,
                },
            ],
        },
    ];

    const doApiRequest = async ({ source, description }: AddRuleProps) => {
        try {
            let sourceValue: string;

            // If source is an array, join them with commas for a single request
            if (Array.isArray(source)) {
                // Filter out empty values
                const validSources = source.filter((ip) => ip && ip.trim());

                if (validSources.length === 0) {
                    throw new Error('No valid IP addresses provided');
                }

                // Join all valid IPs with commas
                sourceValue = validSources.join(',');
            } else if (source) {
                // Handle single IP case
                sourceValue = source.trim();
            } else {
                throw new Error('No valid IP address provided');
            }

            // Make a single API request with all sources
            await FirewallService.addRule({
                uuid,
                source: sourceValue,
                description: description ? description.trim() : undefined,
            });

            notification.open({
                message: 'Add trusted sources',
                description:
                    Array.isArray(source) && source.length > 1
                        ? `New trusted sources successfully added!`
                        : `New trusted source successfully added!`,
                icon: <CcxIconCheckCircle />,
            });

            trackEvent(
                CLICK_EVENT,
                DATASTORE_ADDED_TRUSTED_SOURCE_FIREWALL_SUCCESSFULLY
            );

            return true;
        } catch (e) {
            trackEvent(
                CLICK_EVENT,
                DATASTORE_ADDED_TRUSTED_SOURCE_FIREWALL_FAILED
            );
            notification.open({
                message: 'Add trusted sources',
                description: `There was an error adding your trusted sources. ${e}`,
                icon: <CcxIconCloseCircleTwoTone twoToneColor="#eb2f96" />,
            });

            console.error(e);

            throw e;
        }
    };

    return (
        <Can permission={CAN_ADD_FIREWALL} showTooltip={false}>
            <ButtonModalForm
                title="Create trusted client source addresses"
                buttonText="Create trusted sources"
                buttonIcon={<CcxIconPlusCircleOutlined />}
                onSubmit={doApiRequest}
                onSuccess={onSuccess}
                testId={testId}
                fields={fields}
                submitText="Create"
                formLayout="vertical"
                useGrid={true}
                onFormLoad={setForm}
                disabled={!currentDeployment?.isOperable()}
                overideValidationFunction={overrideValidation}
            >
                <span>
                    <p>
                        Trusted client source addresses are IPs allowed to
                        connect to the datastore.
                    </p>
                    <Alert
                        showIcon={true}
                        message={
                            <div>
                                <p>
                                    You may enter one or more IP addresses (and
                                    optional CIDR ranges) in the Sources field.
                                    Separate multiple entries with a comma or
                                    semicolon.
                                    <br />
                                    For example:
                                    192.168.0.10,10.0.0.1/24;203.0.113.25
                                    <br />
                                    If no mask is specified, <b>/32</b> is used
                                    by default.
                                </p>
                            </div>
                        }
                    />
                </span>
            </ButtonModalForm>
        </Can>
    );
}

export default AddFirewallRule;
